import type { BridgeChartApi } from "../types/public";
import type { BridgeChartInput } from "../types/data";
import { captureSelectedBarAssignment, projectSelectedBarContext } from "../tools/selectedBarContext";

/** Workspace price context is independent of research markers and never grants financial authority. */
export function bindWorkspaceContext(container: HTMLElement, chart: BridgeChartApi, source: BridgeChartInput): () => void {
  const panel = container.querySelector<HTMLElement>("[data-chart-selected-context]");
  const workspace = container.closest?.<HTMLElement>("[data-workspace-symbol][data-workspace-timeframe]");
  const status = panel?.querySelector<HTMLElement>("[data-chart-context-status]");
  const selection = panel?.querySelector<HTMLElement>("[data-chart-context-selection]");
  const supplied = panel?.querySelector<HTMLElement>("[data-chart-context-assignment]");
  if (container.dataset.chartContext !== "workspace" || !panel || !workspace || !status || !selection || !supplied) return () => {};
  const names = ["date", "symbol", "timeframe", "open", "high", "low", "close", "volume", "visible", "basis", "snapshot", "hash", "acquired"] as const;
  const fields = new Map(names.map(name => [name, panel.querySelector<HTMLElement>(`[data-chart-context-field="${name}"]`)]));
  if (names.some(name => !fields.get(name))) return () => {};
  const assignment = captureSelectedBarAssignment(chart.data);
  // The original SSR financial/research context must match this island's exact source,
  // including when only the JSON node is replaced inside a retained container.
  let matchesSource = false;
  try {
    const encoded = panel.dataset.contextIdentity || "";
    if (encoded.length > 8192) throw new Error("ContextIdentityBound");
    const identity = JSON.parse(encoded);
    const financial = (source as BridgeChartInput & { financialOutputStatus?: { allowed?: unknown; blockReason?: { code?: unknown } | null } }).financialOutputStatus;
    const suppliedScalar = (value: unknown) => typeof value === "string" && value.length <= 128 ? value : null;
    // Only the six native families are inspected once at mount, never per candle.
    const availability = source.markerAvailability ?? [];
    const kinds = new Set(["Strategy", "Backtest", "WalkForward", "CompositeAi", "Codal", "MaterialRisk"]);
    const seen = new Set<string>();
    if (!Array.isArray(availability) || availability.length > kinds.size || !availability.every(row => {
      if (!row || !kinds.has(row.kind) || seen.has(row.kind) || typeof row.available !== "boolean"
          || !(row.unavailableReason == null || typeof row.unavailableReason === "string" && row.unavailableReason.length <= 256)) return false;
      seen.add(row.kind); return true;
    })) throw new Error("ContextResearchAvailabilityBound");
    const researchUnavailableReason = availability.some(row => !row.available
      && (row.unavailableReason === "HistoricalPriceBasisChanged" || row.unavailableReason === "FinancialOutputBlocked:HistoricalPriceBasisChanged"))
      ? "HistoricalPriceBasisChanged" : null;
    matchesSource = identity.researchUnavailableReason === researchUnavailableReason && identity.symbol === source.metadata?.symbol && identity.timeframe === source.metadata?.timeframe
      && ["priceBasis", "historySourceSnapshotId", "historySourceHash", "historyAcquiredAt"].every(key => identity[key] === suppliedScalar((source.metadata as unknown as Record<string, unknown> | undefined)?.[key]))
      && typeof identity.allowed === "boolean" && identity.allowed === financial?.allowed
      && identity.blockCode === (financial?.blockReason?.code ?? null)
      && Array.isArray(source.warnings) && source.warnings.length <= 16 && source.warnings.every(warning => typeof warning === "string" && warning.length <= 256)
      && JSON.stringify(identity.warnings) === JSON.stringify(source.warnings);
  } catch { /* Missing/mismatched SSR source has no enhanced financial context. */ }
  let disposed = false, valid = matchesSource;
  const put = (name: typeof names[number], value: string) => { fields.get(name)!.textContent = value; };
  const update = (event: unknown) => {
    if (disposed) return;
    if (event && typeof event === "object" && "state" in event && event.state === "Cleared" && "reason" in event
        && (event.reason === "Data" || event.reason === "PriceBasis")) valid = false;
    if (workspace.dataset.workspaceSymbol !== assignment.metadataSymbol || workspace.dataset.workspaceTimeframe !== assignment.metadataTimeframe) valid = false;
    const context = valid ? projectSelectedBarContext(assignment, chart.data, chart.viewport, event) : { state: "ContextUnavailable" as const };
    if (context.state === "ContextUnavailable") valid = false;
    panel.dataset.contextState = context.state;
    selection.hidden = context.state !== "Selected";
    supplied.hidden = !valid;
    status.textContent = context.state === "Selected" ? context.bar.date
      : valid ? panel.dataset.contextPrompt || "" : panel.dataset.contextUnavailable || "";
    for (const name of names.slice(0, 9)) put(name, "");
    if (context.state === "ContextUnavailable") {
      for (const name of names.slice(9)) put(name, "");
      return;
    }
    const absent = panel.dataset.contextNotSupplied || "";
    for (const name of ["basis", "snapshot", "hash", "acquired"] as const) put(name, context.source[name] || absent);
    if (context.state !== "Selected") return;
    put("date", context.bar.date); put("symbol", context.symbol); put("timeframe", context.timeframe);
    for (const name of ["open", "high", "low", "close", "volume"] as const) put(name, String(context.bar[name]));
    put("visible", `${context.visibleFirst} — ${context.visibleLast}`);
  };
  const offInspection = chart.on("inspection", update), offData = chart.on("data", () => update(null));
  const dispose = () => {
    if (disposed) return;
    update(null); selection.hidden = true; panel.dataset.contextState = valid ? "AwaitingSelection" : "ContextUnavailable";
    disposed = true; offInspection(); offData(); offDestroy();
  };
  const offDestroy = chart.on("destroy", dispose);
  update(null);
  return dispose;
}
